import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import { useRef, useState } from 'react';
import { useFormik, FormikProvider } from 'formik';
import shareIcon from '@iconify/icons-eva/share-outline';
import copyFillIcon from '@iconify/icons-eva/copy-fill';
// material
import {
  Box,
  Typography,
  Tooltip,
  Input,
  InputAdornment,
  FormControl,
  Divider,
  TextField,
  MenuItem,
  ListItemText
} from '@mui/material';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import { CopyToClipboard } from 'react-copy-to-clipboard';

import { LoadingButton } from '@mui/lab';
import { createWithQuery, getItem } from '../../fetch';
import MenuPopover from '../MenuPopover';
import { useEffectAsync } from '../../reactHelper';
import { useTranslation } from '../../common/LocalizationProvider';

// ----------------------------------------------------------------------

FlightReviewShare.propTypes = {
  reportTrip: PropTypes.object.isRequired,
  setIsOpenSpeedDial: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default function FlightReviewShare({ reportTrip, setIsOpenSpeedDial, disabled }) {
  const t = useTranslation();
  const ref = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [isOpen, setIsOpen] = useState(false);

  const RegisterSchema = Yup.object().shape({
    email: Yup.string().email(t('RGF_Email_Required')).required(t('RGF_Email_Required'))
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      urlShare: ''
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      if (!disabled) {
        const response = await createWithQuery(
          `share/mail/url?email=${values.email}&deviceId=${reportTrip.deviceId}&from=${moment(
            reportTrip.startTime
          ).toISOString()}&to=${moment(reportTrip.endTime).toISOString()}`
        );
        if (response.ok) {
          enqueueSnackbar(t('FR_Share_SendMailSuccess'), { variant: 'success' });
        } else {
          enqueueSnackbar(t('FR_Share_SendMailError'), { variant: 'error' });
        }
      }
      setSubmitting(false);
      setIsOpen(false);
      setIsOpenSpeedDial(false);
    }
  });
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  useEffectAsync(async () => {
    if (isOpen && !disabled) {
      const response = await getItem(
        `share/copy/url?deviceId=${reportTrip.deviceId}&from=${moment(
          reportTrip.startTime
        ).toISOString()}&to=${moment(reportTrip.endTime).toISOString()}`
      );
      if (response.ok) {
        setFieldValue('urlShare', await response.text());
      }
    }
  }, [isOpen]);

  const handleCopy = () => {
    setIsOpen(false);
    setIsOpenSpeedDial(false);
    enqueueSnackbar(t('FR_Share_CopiedClipboard'), { variant: 'success' });
  };

  return (
    <>
      <MenuItem
        sx={{ color: 'text.secondary' }}
        onClick={() => setIsOpen(true)}
        ref={ref}
        disabled={disabled}
      >
        <Icon icon={shareIcon} width={23} height={23} />

        <ListItemText
          primary={t('FR_Tooltip_Share')}
          primaryTypographyProps={{ variant: 'body2' }}
        />
      </MenuItem>

      <MenuPopover
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={ref.current}
        sx={{ width: 400 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {t('FR_Share_CopyLink')}
          </Typography>
          <FormControl fullWidth>
            <Input
              value={getFieldProps('urlShare').value}
              endAdornment={
                <InputAdornment position="end">
                  <CopyToClipboard text={getFieldProps('urlShare').value} onCopy={handleCopy}>
                    <Tooltip title={t('btn_Copy')} placement="top">
                      <Icon icon={copyFillIcon} width={30} height={30} />
                    </Tooltip>
                  </CopyToClipboard>
                </InputAdornment>
              }
            />
          </FormControl>
        </Box>

        <Divider sx={{ my: 1 }} />

        <FormikProvider value={formik}>
          <Box sx={{ my: 1.5, px: 2.5 }}>
            <Typography variant="subtitle1" noWrap>
              {t('FR_Share_SendViaEmail')}
            </Typography>
          </Box>
          <Box sx={{ my: 1.5, px: 2.5 }}>
            <TextField
              fullWidth
              autoComplete="email"
              type="email"
              label={t('fields_Email')}
              {...getFieldProps('email')}
              value={getFieldProps('email').value || ''}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
            />
          </Box>
          <Box sx={{ p: 2, pt: 1.5 }}>
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              onClick={handleSubmit}
            >
              {t('RGF_Submit')}
            </LoadingButton>
          </Box>
        </FormikProvider>
      </MenuPopover>
    </>
  );
}
